import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import {
  MrfiktivSendMessageResultViewModelGen,
  MrfiktivSendSmsDtoGen,
  MrfiktivSendWhatsAppDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ConfigModule } from "@/store/modules/config";
import { PhoneMessage as MrfiktivPhoneMessage } from "../mrfiktiv/v1/PhoneMessage";
import { HttpClient as MrFiktivClient } from "../mrfiktiv/v1/http-client";

/**
 * Communicates with the phone message endpoints (SMS and WhatsApp)
 */
class PhoneMessageService {
  mrfiktivClient: MrFiktivClient;

  /**
   * The proxy for MrFiktiv phone message operations.
   */
  mrfiktivProxy: MrfiktivPhoneMessage;

  /**
   * @class Initialize PhoneMessageService
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivClient = mrfiktivHttpClientProvider.client();
    this.mrfiktivProxy = new MrfiktivPhoneMessage(this.mrfiktivClient);
  }

  /**
   * Sends an SMS message to the specified phone number
   * @param partnerId - The partner ID
   * @param data - SMS message data including identifier, recipient, and message body
   * @returns Promise with send message result
   */
  async sendSms(partnerId: string, data: MrfiktivSendSmsDtoGen): Promise<MrfiktivSendMessageResultViewModelGen> {
    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      return (await this.mrfiktivProxy.phoneMessageControllerSendSms(partnerId, data)).data;
    }

    // THG doesn't currently support phone messages
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      throw new Error("Phone messaging is not supported in THG context");
    }

    throw new Error("Phone messaging is not supported");
  }

  /**
   * Sends a WhatsApp message to the specified phone number
   * Requires an active session (customer must have messaged first within 24h)
   * @param partnerId - The partner ID
   * @param data - WhatsApp message data including identifier, recipient, and message body
   * @returns Promise with send message result
   */
  async sendWhatsApp(
    partnerId: string,
    data: MrfiktivSendWhatsAppDtoGen
  ): Promise<MrfiktivSendMessageResultViewModelGen> {
    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      return (await this.mrfiktivProxy.phoneMessageControllerSendWhatsApp(partnerId, data)).data;
    }

    // THG doesn't currently support phone messages
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      throw new Error("Phone messaging is not supported in THG context");
    }

    throw new Error("Phone messaging is not supported");
  }

  /**
   * Sends multiple SMS messages
   * @param partnerId - The partner ID
   * @param messages - Array of SMS message data
   * @returns Promise with array of send message results
   */
  async sendMultipleSms(
    partnerId: string,
    messages: MrfiktivSendSmsDtoGen[]
  ): Promise<MrfiktivSendMessageResultViewModelGen[]> {
    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      const results = await Promise.all(
        messages.map(message => this.mrfiktivProxy.phoneMessageControllerSendSms(partnerId, message))
      );

      return results.map(result => result.data);
    }

    // THG doesn't currently support phone messages
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      throw new Error("Phone messaging is not supported in THG context");
    }

    return [];
  }

  /**
   * Sends multiple WhatsApp messages
   * @param partnerId - The partner ID
   * @param messages - Array of WhatsApp message data
   * @returns Promise with array of send message results
   */
  async sendMultipleWhatsApp(
    partnerId: string,
    messages: MrfiktivSendWhatsAppDtoGen[]
  ): Promise<MrfiktivSendMessageResultViewModelGen[]> {
    if (ConfigModule.appContext === AppContextEnum.REPORT_PORTAL) {
      const results = await Promise.all(
        messages.map(message => this.mrfiktivProxy.phoneMessageControllerSendWhatsApp(partnerId, message))
      );

      return results.map(result => result.data);
    }

    // THG doesn't currently support phone messages
    if (ConfigModule.appContext === AppContextEnum.THG_PORTAL) {
      throw new Error("Phone messaging is not supported in THG context");
    }

    return [];
  }
}

export default new PhoneMessageService(new MrfiktivHttpClientProvider());
